import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Send, MessageSquare, ChevronDown } from "lucide-react";
import { api, getCurrentUser } from "../api";
import type { ChatMessage, Member } from "../types";

/** Format ISO timestamp to DD/MM/YYYY HH:MM */
function fmtTimestamp(iso: string): string {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
}

/** Format relative time for recent messages */
function fmtRelative(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diffMin = Math.floor((now - then) / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return fmtTimestamp(iso);
}

/** Render message content with @mentions as clickable links */
function MessageContent({ content, mentions }: { content: string; mentions: ChatMessage["mentions"] }) {
  const navigate = useNavigate();
  if (!mentions || mentions.length === 0) {
    return <span>{content}</span>;
  }

  const mentionNames = mentions.map((m) => m.member_name);
  const pattern = mentionNames.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
  const regex = new RegExp(`@(${pattern})`, "g");

  const parts: (string | React.JSX.Element)[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push(content.slice(lastIndex, match.index));
    }
    const memberName = match[1];
    const mention = mentions.find((m) => m.member_name === memberName);
    if (mention) {
      parts.push(
        <button
          key={match.index}
          onClick={() => navigate(`/members/${mention.member_id}`)}
          className="inline font-medium text-white hover:underline bg-[#5f1b2c] px-1 rounded"
        >
          @{memberName}
        </button>
      );
    } else {
      parts.push(match[0]);
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex));
  }

  return <span>{parts}</span>;
}

export function Chat() {
  const currentUser = getCurrentUser();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [members, setMembers] = useState<Member[]>([]);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [showMentions, setShowMentions] = useState(false);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const pendingMentionsRef = useRef<{ member_id: string; member_name: string }[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const lastSeenRef = useRef<string>("");
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    api.getMembers().then((r) => setMembers(r.members)).catch(() => {});
  }, []);

  // Merge new messages into state, skipping any that already exist by ID
  const mergeMessages = (prev: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] => {
    if (incoming.length === 0) return prev;
    const existingIds = new Set(prev.map((m) => m.id));
    const newMsgs = incoming.filter((m) => !existingIds.has(m.id));
    return newMsgs.length > 0 ? [...prev, ...newMsgs] : prev;
  };

  useEffect(() => {
    api.getChatMessages({ limit: 100 })
      .then((r) => {
        setMessages(r.messages);
        if (r.messages.length > 0) {
          lastSeenRef.current = r.messages[r.messages.length - 1].created_at;
        }
        setLoading(false);
        setTimeout(() => scrollToBottom(), 100);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    pollRef.current = setInterval(async () => {
      if (!lastSeenRef.current) return;
      try {
        const res = await api.pollChatMessages(lastSeenRef.current);
        if (res.messages.length > 0) {
          setMessages((prev) => mergeMessages(prev, res.messages));
          lastSeenRef.current = res.messages[res.messages.length - 1].created_at;
          const container = chatContainerRef.current;
          const nearBottom = container ? container.scrollHeight - container.scrollTop - container.clientHeight < 100 : true;
          if (nearBottom) {
            setTimeout(() => scrollToBottom(), 50);
          } else {
            setHasNewMessages(true);
          }
        }
      } catch {}
    }, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setInput(val);
    const cursorPos = e.target.selectionStart || val.length;
    const textBeforeCursor = val.slice(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);
    if (atMatch) {
      setMentionQuery(atMatch[1].toLowerCase());
      setMentionIndex(0);
      setShowMentions(true);
    } else {
      setShowMentions(false);
      setMentionQuery(null);
    }
  };

  const filteredMembers = mentionQuery !== null
    ? members.filter((m) => m.name.toLowerCase().includes(mentionQuery)).slice(0, 6)
    : members.slice(0, 6);

  const selectMention = (member: Member) => {
    const cursorPos = inputRef.current?.selectionStart || input.length;
    const textBeforeCursor = input.slice(0, cursorPos);
    const textAfterCursor = input.slice(cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf("@");
    const newText = textBeforeCursor.slice(0, atIndex) + `@${member.name} ` + textAfterCursor;
    setInput(newText);
    setShowMentions(false);
    setMentionQuery(null);
    // Track this mention for when the message is sent
    pendingMentionsRef.current = [
      ...pendingMentionsRef.current.filter((m) => m.member_id !== member.id),
      { member_id: member.id, member_name: member.name },
    ];
    inputRef.current?.focus();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (showMentions && filteredMembers.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setMentionIndex((i) => Math.min(i + 1, filteredMembers.length - 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setMentionIndex((i) => Math.max(i - 1, 0)); }
      else if (e.key === "Tab") { e.preventDefault(); selectMention(filteredMembers[mentionIndex]); }
      else if (e.key === "Escape") { setShowMentions(false); }
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleSend = async () => {
    const content = input.trim();
    if (!content || sending) return;
    // Match any @Name in the text against known members
    const mentions = members
      .filter((m) => content.includes(`@${m.name}`))
      .map((m) => ({ member_id: m.id, member_name: m.name }));
    setSending(true);
    setInput("");
    setShowMentions(false);
    pendingMentionsRef.current = [];
    try {
      const res = await api.sendChatMessage(content, mentions.length > 0 ? mentions : undefined);
      setMessages((prev) => mergeMessages(prev, [res.message]));
      lastSeenRef.current = res.message.created_at;
      setHasNewMessages(false);
      setTimeout(() => scrollToBottom(), 50);
    } catch (e) {
      console.error(e);
      setInput(content);
    } finally {
      setSending(false);
    }
  };

  const handleScroll = () => {
    const container = chatContainerRef.current;
    if (!container) return;
    const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    if (nearBottom) setHasNewMessages(false);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] relative">
      <div className="bg-white border border-gray-200 rounded-t-xl px-5 py-3 flex items-center gap-3 shadow-sm">
        <MessageSquare className="w-5 h-5 text-[#5f1b2c]" />
        <h1 className="text-lg font-bold text-gray-900">Team Chat</h1>
        <span className="text-xs text-gray-400 ml-1">Shared channel for all coaches</span>
      </div>

      <div ref={chatContainerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto bg-gray-50 border-x border-gray-200 px-5 py-4 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#5f1b2c] border-t-transparent" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <MessageSquare className="w-10 h-10 mb-3 text-gray-300" />
            <p className="text-sm">No messages yet. Start the conversation!</p>
          </div>
        ) : (
          <>
            {messages.map((msg) => {
              const isMe = msg.sender_id === currentUser?.id;
              const isSystem = msg.sender_type === "system";
              return (
                <div key={msg.id} className={`flex flex-col ${isMe ? "items-end" : "items-start"}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {!isMe && (
                      <span className={`text-xs font-semibold ${isSystem ? "text-gray-500 italic" : "text-[#5f1b2c]"}`}>
                        {isSystem ? "\u{1F916} System" : msg.sender_name}
                      </span>
                    )}
                    <span className="text-[10px] text-gray-400">{fmtRelative(msg.created_at)}</span>
                  </div>
                  <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    isSystem ? "bg-gray-100 text-gray-500 italic border border-gray-200"
                    : isMe ? "bg-[#5f1b2c] text-white rounded-br-md"
                    : "bg-white text-gray-900 border border-gray-200 rounded-bl-md shadow-sm"
                  }`}>
                    <MessageContent content={msg.content} mentions={msg.mentions} />
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {hasNewMessages && (
        <button onClick={() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); setHasNewMessages(false); }} className="absolute bottom-24 left-1/2 -translate-x-1/2 bg-[#5f1b2c] text-white text-xs font-medium px-4 py-2 rounded-full shadow-lg hover:bg-[#4a1520] transition-colors flex items-center gap-1.5 z-10">
          <ChevronDown className="w-3.5 h-3.5" /> New messages
        </button>
      )}

      {showMentions && filteredMembers.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg mx-5 mb-1 max-h-48 overflow-y-auto z-20">
          {filteredMembers.map((m, i) => (
            <button key={m.id} onClick={() => selectMention(m)} className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors ${i === mentionIndex ? "bg-[#e2c4b6]/30 text-[#5f1b2c]" : "hover:bg-gray-50 text-gray-700"}`}>
              <span className="w-7 h-7 rounded-full bg-[#e2c4b6]/40 flex items-center justify-center text-[#5f1b2c] font-bold text-[10px] shrink-0">
                {m.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
              </span>
              <div>
                <span className="font-medium">{m.name}</span>
                <span className="text-xs text-gray-400 ml-2">{m.membership_type || m.payment_plan || ""}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-b-xl px-5 py-3 shadow-sm">
        <div className="flex items-end gap-3">
          <textarea
            ref={inputRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            placeholder="Type a message... (use @ to tag a member)"
            rows={1}
            className="flex-1 resize-none bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#5f1b2c] focus:ring-1 focus:ring-[#5f1b2c]/30 max-h-32"
            style={{ minHeight: "40px" }}
            onInput={(e) => { const t = e.target as HTMLTextAreaElement; t.style.height = "auto"; t.style.height = Math.min(t.scrollHeight, 128) + "px"; }}
          />
          <button onClick={handleSend} disabled={!input.trim() || sending} className="p-2.5 bg-[#5f1b2c] text-white rounded-xl hover:bg-[#4a1520] transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}